import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import * as Yup from "yup";
import { mergeConsultantLists } from "../../../../utils/mergeConsultantLists";
import getValidationErrors from "../../../../utils/getValidationError";
import api from "../../../../services/api";
import { useAuth } from "../../../../hooks/auth";
import { useToast } from "../../../../hooks/toast";
import refreshToken from "../../../../utils/refreshToken";
import { useConsultant } from "../../../../hooks/consultant";
import { useHistory } from "react-router-dom";
import {
  HOUSING_PROGRAM_NONE,
  normalizeHousingProgramForApi,
  normalizeHousingProgramFromApi,
} from "../../../../utils/housingProgram";
import FloorTypeOptions from "./enums/FloorTypeOptions";
import ModalAddOwner, {
  type ModalAddOwnerHandle,
} from "./ModalAddOwner";
import {
  fetchKeyLocations,
  patchPropertyKeyLocation,
  type KeyLocationCatalogItem,
} from "../../../../services/propertyKeyLocation";
import {
  applyKeyLocationToCreatePayload,
  buildKeyLocationPatchBody,
  isDigitalKeyLocation,
  isOthersKeyLocation,
  KEY_LOCATION_PASSWORD_REQUIRED_MESSAGE,
  validateKeyLocationNoteForOthers,
  validateKeyLocationPasswordForDigital,
} from "../../../../utils/keyLocationRules";
import { readPropertyKeyLocationPassword } from "../../../../utils/visitKeyLocation";
import { saveDraft } from "../../../../services/propertyDraft";
import { addPropertyChildren } from "../../../../services/propertyChildren";
import type { PropertyChildInput } from "../../../../types/propertyChildren";
import { validateChildrenList } from "../../../../utils/propertyChildren";
import { usePropertyAutoSave } from "../../../../hooks/usePropertyAutoSave";
import { useOwnerEmailSearch } from "../../../../hooks/useOwnerEmailSearch";
import AutoSaveIndicator from "../../../../components/AutoSaveIndicator";
import SaveDraftButton from "../../../../components/SaveDraftButton";
import {
  applyPropertyOwnerToPayload,
  resolveOwnerStateAfterDraftSave,
  type PropertyOwnerUserData,
} from "../../../../utils/propertyOwnerPayload";
import { sanitizePropertyDescriptionField } from "../../../../utils/propertyPlainTextSanitizer";
import {
  applyPropertyAlternativeContactToPayload,
  normalizePropertyAlternativeContactFromApi,
  optionalAlternativeContactYupFields,
  validatePropertyAlternativeContact,
} from "../../../../utils/propertyAlternativeContact";
import { validateDraftMinimum } from "../../../../utils/propertyDraftValidation";
import { yupPropertyAreaString } from "../../../../utils/propertyArea";
import { parseFurnitureStatus } from "../../../../utils/propertyFurniture";
import {
  applyCommissionToPayload,
  commissionFormFromApi,
  emptyCommissionForm,
  firstCommissionError,
  validatePropertyCommission,
  type PropertyCommissionErrors,
  type PropertyCommissionForm,
} from "../../../../utils/propertyCommission";
import {
  FormStack,
  PropertyStepActions,
} from "../../../../components/propertyDescription/descriptionUi";
import { NextButton, ResetButton } from "../../twStyles";
import ClassificationSection from "./sections/ClassificationSection";
import ResponsiblesSection from "./sections/ResponsiblesSection";
import PublicationSection from "./sections/PublicationSection";
import ValuesSection from "./sections/ValuesSection";
import CommissionSection from "../../../../components/propertyDescription/CommissionSection";
import GuaranteesOnlySection from "./sections/GuaranteesOnlySection";
import CharacteristicsForm from "../CharacteristicsForm";

type IUserData = PropertyOwnerUserData;

interface IFinder {
  id: number;
  first_name: string;
}

interface IConsultant {
  id: number;
  first_name: string;
}

interface IField {
  [key: string]: any;
}

interface IProps {
  phase: "property" | "publication" | "characteristics";
  address_id: number | undefined;
  propertyID?: number;
  initialProperty?: Record<string, unknown>;
  localChildren: PropertyChildInput[];
  onLocalChildrenChange: (next: PropertyChildInput[]) => void;
  onGoToProperty: () => void;
  onGoToPublication: () => void;
  onGoToPhotos: () => void;
  onChangeCurrentForm: () => void;
  onPropertyIdReceived: (id: number) => void;
}

function deriveConsultant(
  consultantId: number | undefined,
  consultantsList: IConsultant[]
) {
  const selectedConsultant = consultantsList.find(
    (consultant) => consultant.id === consultantId
  );
  return selectedConsultant
    ? { label: selectedConsultant.first_name, value: selectedConsultant.id }
    : undefined;
}

const defaultProperty = {
  property_type_id: 1,
  floor_type: FloorTypeOptions.LOW,
  housing_program: HOUSING_PROGRAM_NONE,
  consultant_id: undefined,
  finder_id: undefined,
  address_id: undefined,
  title: "",
  description: "",
  rooms: undefined,
  suites: undefined,
  bathrooms: undefined,
  garages: undefined,
  year_built: "",
  rent_value: 0,
  condominium_value: 0,
  iptu_value: 0,
  garbage_tax: 0,
  area: "0",
  is_furnished: 0,
  allow_pets: 0,
  is_adm: 0,
  rank: undefined,
  zap_publication_type: "STANDARD",
  water_included: 0,
  gas_included: 0,
  guarantees: [1],
  condominium: "",
  virtual_tour_url: "",
  key_location_id: undefined as number | undefined,
  key_location_note: "",
  key_location_password: "",
  note: "",
  user_id: undefined as number | undefined,
  partner_broker_id: undefined as number | undefined,
  user_data: undefined as IUserData | undefined,
  contact_email: "",
  contact_phone: "",
  ...emptyCommissionForm(),
};

function buildPropertyCreatePayload(
  formatted: Record<string, unknown>,
  condominium: string,
  ownerMode: { user_id?: number; partner_broker_id?: number; user_data?: IUserData }
) {
  const {
    user_data: _ud,
    user_id: _uid,
    partner_broker_id: _pbid,
    ...rest
  } = formatted;

  const payload: Record<string, unknown> = {
    ...rest,
    condominium,
  };

  return applyPropertyOwnerToPayload(payload, ownerMode);
}

function toFormCurrencyValue(value: unknown): number {
  if (value == null || value === "") return 0;
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  if (Number.isInteger(num) && String(value).length > 4) return num;
  return Math.round(num * 100);
}

function mapInitialProperty(raw: Record<string, unknown>): IField {
  return {
    ...defaultProperty,
    property_type_id: Number(raw.property_type_id ?? defaultProperty.property_type_id),
    floor_type: String(raw.floor_type ?? defaultProperty.floor_type),
    housing_program: normalizeHousingProgramFromApi(raw.housing_program),
    consultant_id: raw.consultant_id != null ? Number(raw.consultant_id) : undefined,
    finder_id: raw.finder_id != null ? Number(raw.finder_id) : undefined,
    address_id: raw.address_id != null ? Number(raw.address_id) : undefined,
    title: String(raw.title ?? ""),
    description: String(raw.description ?? ""),
    rooms: raw.rooms != null ? Number(raw.rooms) : undefined,
    suites: raw.suites != null ? Number(raw.suites) : undefined,
    bathrooms: raw.bathrooms != null ? Number(raw.bathrooms) : undefined,
    garages: raw.garages != null ? Number(raw.garages) : undefined,
    year_built: raw.year_built != null ? String(raw.year_built) : "",
    rent_value: toFormCurrencyValue(raw.rent_value),
    condominium_value: toFormCurrencyValue(raw.condominium_value),
    iptu_value: toFormCurrencyValue(raw.iptu_value),
    garbage_tax: toFormCurrencyValue(raw.garbage_tax),
    area: raw.area != null ? String(raw.area) : defaultProperty.area,
    is_furnished: parseFurnitureStatus(raw.is_furnished),
    allow_pets: Number(raw.allow_pets ?? 0),
    is_adm: Number(raw.is_adm ?? 0),
    rank: raw.rank != null ? Number(raw.rank) : undefined,
    zap_publication_type: String(raw.zap_publication_type ?? "STANDARD"),
    water_included: Number(raw.water_included ?? 0),
    gas_included: Number(raw.gas_included ?? 0),
    guarantees: Array.isArray(raw.guarantees)
      ? raw.guarantees.map((g) => Number(g))
      : defaultProperty.guarantees,
    condominium: String(raw.condominium ?? ""),
    virtual_tour_url: String(raw.virtual_tour_url ?? ""),
    key_location_id:
      raw.key_location_id != null ? Number(raw.key_location_id) : undefined,
    key_location_note: String(raw.key_location_note ?? ""),
    key_location_password: readPropertyKeyLocationPassword(raw),
    ...commissionFormFromApi(raw),
    user_id: raw.user_id != null ? Number(raw.user_id) : undefined,
    partner_broker_id:
      raw.partner_broker_id != null ? Number(raw.partner_broker_id) : undefined,
    note: raw.note != null ? String(raw.note) : "",
    ...normalizePropertyAlternativeContactFromApi({
      contact_email:
        typeof raw.contact_email === "string" ? raw.contact_email : null,
      contact_phone:
        typeof raw.contact_phone === "string" ? raw.contact_phone : null,
    }),
  };
}

function DescriptionForm({
  phase,
  address_id,
  propertyID,
  initialProperty,
  localChildren,
  onLocalChildrenChange,
  onGoToProperty,
  onGoToPublication,
  onGoToPhotos,
  onChangeCurrentForm,
  onPropertyIdReceived,
}: IProps) {
  const { access_token, dateToExpires, user } = useAuth();
  const { addToast } = useToast();
  const { consultantsSaved, handleAddConsultants } = useConsultant();
  const history = useHistory();

  const [property, setProperty] = useState(() =>
    initialProperty ? mapInitialProperty(initialProperty) : (defaultProperty as IField)
  );
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isFetchingConsultants, setIsFetchingConsultants] = useState(true);
  const [briefDescription, setBriefDescription] = useState("");
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [consultants, setConsultants] = useState<IConsultant[]>([]);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState("");
  const [alternativeContactErrors, setAlternativeContactErrors] = useState<
    Record<string, string>
  >({});
  const [commissionErrors, setCommissionErrors] =
    useState<PropertyCommissionErrors>({});
  /** E-mail exibido no campo quando o dono veio só da busca (sem `user_data` no payload). */
  const [ownerEmailDisplay, setOwnerEmailDisplay] = useState("");
  const [finders, setFinders] = useState<IFinder[]>([]);
  const [loadingFinders, setLoadingFinders] = useState(true);
  const [keyLocationCatalog, setKeyLocationCatalog] = useState<
    KeyLocationCatalogItem[]
  >([]);

  const dialog = useRef<ModalAddOwnerHandle>(null);

  const {
    results: owners,
    isFetching: isFetchingOwners,
    isOpen: isOwnerDropdownOpen,
    closeDropdown: closeOwnerDropdown,
    unlockSearch: unlockOwnerSearch,
    onSearchInputChange,
  } = useOwnerEmailSearch({
    accessToken: access_token,
    dateToExpires,
    onEmptyResults: () => {
      addToast({
        title: "Nenhum usuário encontrado",
        type: "error",
      });
    },
  });

  const hasSelectedOwner = !!property.user_data || !!property.user_id;

  const consultantsOptions = consultants.map((consultant) => ({
    label: consultant.first_name,
    value: consultant.id,
  }));

  const findersOptions = useMemo(() => {
    if (!Array.isArray(finders)) return [];
    return finders.map((finder) => ({
      label: finder.first_name,
      value: finder.id,
    }));
  }, [finders]);

  const loadFinders = useCallback(async () => {
    try {
      setLoadingFinders(true);
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      const response = await api.get("/api/adm/users/finders");
      const findersData = response.data.data.finders;
      setFinders(findersData);
    } catch (err: any) {
      setFinders([]);
    } finally {
      setLoadingFinders(false);
    }
  }, [access_token, dateToExpires]);

  useEffect(() => {
    async function loadApi() {
      try {
        setIsFetchingConsultants(true);

        const token = await refreshToken(dateToExpires, access_token);

        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

        if (consultantsSaved.length < 1) {
          const [consultantsRes, brokersRes] = await Promise.all([
            api.get("/api/adm/users", {
              params: {
                role: "consultant",
              },
            }),
            api.get("/api/adm/users/partner-brokers").catch(() => ({
              data: { data: { partner_brokers: { data: [] as IConsultant[] } } },
            })),
          ]);

          const merged = mergeConsultantLists(
            consultantsRes.data.data.consultants,
            brokersRes.data?.data?.partner_brokers?.data
          );

          handleAddConsultants(merged);

          setConsultants(merged);
        } else {
          setConsultants(consultantsSaved);
        }
      } catch (err: any) {
        if (!!err.response?.data.message) {
          addToast({
            title: err.response.data.message,
            type: "error",
          });
        }
      }
      setIsFetchingConsultants(false);
    }

    if (!!user) {
      loadApi();
      loadFinders();
      void (async () => {
        try {
          const token = await refreshToken(dateToExpires, access_token);
          api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
          setKeyLocationCatalog(await fetchKeyLocations(api));
        } catch {
          setKeyLocationCatalog([]);
        }
      })();
    } else {
      history.push("/");
    }
  }, [
    consultantsSaved,
    dateToExpires,
    access_token,
    user,
    history,
    handleAddConsultants,
    loadFinders,
    addToast,
  ]);

  const syncKeyLocationIfNeeded = useCallback(
    async (parentId: number) => {
      const keyLocationId =
        property.key_location_id != null
          ? Number(property.key_location_id)
          : null;
      if (keyLocationId == null || !Number.isFinite(keyLocationId)) return;

      const othersErr = validateKeyLocationNoteForOthers(
        keyLocationCatalog,
        keyLocationId,
        property.key_location_note
      );
      if (othersErr) {
        addToast({ title: othersErr, type: "error" });
        return;
      }

      const passwordErr = validateKeyLocationPasswordForDigital(
        keyLocationCatalog,
        keyLocationId,
        property.key_location_password
      );
      if (passwordErr) {
        addToast({ title: passwordErr, type: "error" });
        return;
      }

      await patchPropertyKeyLocation(
        api,
        parentId,
        buildKeyLocationPatchBody(keyLocationCatalog, {
          keyLocationId,
          keyLocationNote: property.key_location_note,
          keyLocationPassword: property.key_location_password,
        })
      );
    },
    [
      addToast,
      keyLocationCatalog,
      property.key_location_id,
      property.key_location_note,
      property.key_location_password,
    ]
  );

  const syncLocalChildrenIfNeeded = useCallback(
    async (parentId: number) => {
      if (localChildren.length === 0) return;

      const listError = validateChildrenList(localChildren);
      if (listError) {
        addToast({ title: listError, type: "error" });
        return;
      }

      await addPropertyChildren(api, parentId, localChildren);
      onLocalChildrenChange([]);
    },
    [addToast, localChildren, onLocalChildrenChange]
  );

  const buildDraftPayload = useCallback(() => {
    const area =
      Number(String(property.area).replaceAll("m²", "").replaceAll(",", ".")) ||
      0;
    const formatted: Record<string, unknown> = {
      ...property,
      address_id,
      area,
      description: sanitizePropertyDescriptionField(property.description),
      housing_program: normalizeHousingProgramForApi(property.housing_program),
      garbage_tax: formatCurrency(property.garbage_tax),
      iptu_value: formatCurrency(property.iptu_value),
      rent_value: formatCurrency(property.rent_value),
      condominium_value: formatCurrency(property.condominium_value),
    };
    delete (formatted as { key_location?: unknown }).key_location;
    delete (formatted as { key_location_password?: unknown }).key_location_password;
    const withCommission = applyCommissionToPayload(
      formatted,
      commissionFormFromApi(property)
    );
    const ownerMode = {
      user_id: property.user_id,
      partner_broker_id: property.partner_broker_id,
      user_data: property.user_data,
    };
    return applyPropertyAlternativeContactToPayload(
      buildPropertyCreatePayload(withCommission, property.condominium, ownerMode),
      {
        contact_email: String(property.contact_email ?? ""),
        contact_phone: String(property.contact_phone ?? ""),
      }
    );
  }, [property, address_id]);

  const syncOwnerFromDraftSave = useCallback((savedUserId: number | undefined) => {
    if (savedUserId == null) return;

    setProperty((prev) => {
      const nextOwner = resolveOwnerStateAfterDraftSave(
        {
          user_id: prev.user_id,
          partner_broker_id: prev.partner_broker_id,
          user_data: prev.user_data,
        },
        savedUserId
      );

      if (
        nextOwner.user_id === prev.user_id &&
        nextOwner.user_data === prev.user_data
      ) {
        return prev;
      }

      return {
        ...prev,
        user_id: nextOwner.user_id,
        partner_broker_id: nextOwner.partner_broker_id,
        user_data: nextOwner.user_data,
      };
    });
  }, []);

  const performDraftSave = useCallback(async () => {
    const contactErrors = validatePropertyAlternativeContact({
      contact_email: String(property.contact_email ?? ""),
      contact_phone: String(property.contact_phone ?? ""),
    });
    if (Object.keys(contactErrors).length > 0) {
      setAlternativeContactErrors(contactErrors);
      return;
    }
    setAlternativeContactErrors({});

    const nextCommissionErrors = validatePropertyCommission(
      commissionFormFromApi(property)
    );
    if (Object.keys(nextCommissionErrors).length > 0) {
      setCommissionErrors(nextCommissionErrors);
      return;
    }
    setCommissionErrors({});

    const errors = validateDraftMinimum(buildDraftPayload());
    if (errors.length > 0) return;

    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
    const result = await saveDraft(api, buildDraftPayload(), propertyID ?? null);
    if (!result.id) return;

    syncOwnerFromDraftSave(result.user_id);

    // Persist the draft id first so a later sync failure cannot create a duplicate draft.
    onPropertyIdReceived(result.id);

    try {
      await syncKeyLocationIfNeeded(result.id);
      await syncLocalChildrenIfNeeded(result.id);
    } catch (err: any) {
      addToast({
        title:
          err?.response?.data?.message ??
          "Rascunho criado, mas falhou ao sincronizar chaves/filhos.",
        type: "error",
      });
    }
  }, [
    access_token,
    addToast,
    dateToExpires,
    buildDraftPayload,
    propertyID,
    onPropertyIdReceived,
    syncKeyLocationIfNeeded,
    syncLocalChildrenIfNeeded,
    syncOwnerFromDraftSave,
    property,
  ]);

  const {
    isAutoSaving,
    lastSavedAt,
    hasUnsavedChanges,
    hasError,
    markChanged,
    saveNow,
  } = usePropertyAutoSave({
    enabled: !!address_id,
    save: performDraftSave,
  });

  function handleChange(identifier: string, value: any) {
    setProperty((prevValue) => ({ ...prevValue, [identifier]: value }));
    markChanged();
  }

  async function handleSaveDraft() {
    if (isSavingDraft) return;

    const nextCommissionErrors = validatePropertyCommission(
      commissionFormFromApi(property)
    );
    if (Object.keys(nextCommissionErrors).length > 0) {
      setCommissionErrors(nextCommissionErrors);
      addToast({
        title: firstCommissionError(nextCommissionErrors),
        type: "error",
      });
      return;
    }
    setCommissionErrors({});

    const errors = validateDraftMinimum(buildDraftPayload());
    if (errors.length > 0) {
      addToast({ title: errors[0], type: "error" });
      return;
    }

    setIsSavingDraft(true);
    try {
      await saveNow();
      addToast({ title: "Rascunho salvo com sucesso!", type: "success" });
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao salvar rascunho",
        type: "error",
      });
    } finally {
      setIsSavingDraft(false);
    }
  }

  function handleSelectOwner(userId: number | undefined) {
    const user_selected = owners.find((owner) => owner.id === userId);
    closeOwnerDropdown();

    if (user_selected) {
      setProperty((prev) => ({
        ...prev,
        user_id: user_selected.id,
        partner_broker_id: user_selected.id,
        user_data: undefined,
      }));
      setOwnerEmailDisplay(user_selected.email ?? "");
      setOwnerSearchTerm("");
    }
  }

  function handleGuaranteeChange(event: any) {
    if (event.target.checked) {
      setProperty((prevValue) => ({
        ...prevValue,
        guarantees: [...prevValue.guarantees, Number(event.target.value)],
      }));
    } else
      setProperty((prevValue) => ({
        ...prevValue,
        guarantees: prevValue.guarantees.filter((guarantee: number) => {
          return Number(event.target.value) !== guarantee;
        }),
      }));
  }

  function formatCurrency(value: number) {
    const stringValue = value.toString();
    return Number(
      `${stringValue.substring(
        0,
        stringValue.length - 2
      )}.${stringValue.substring(stringValue.length - 2, stringValue.length)}`
    );
  }

  function handleOpenNewOwnerModal() {
    dialog.current?.open();
  }

  function handleNewOwnerFromModal(_identifier: string, userData: IUserData) {
    closeOwnerDropdown();
    setProperty((prev) => ({
      ...prev,
      user_data: userData,
      user_id: undefined,
      partner_broker_id: undefined,
    }));
    setOwnerEmailDisplay(userData.email ?? "");
    setOwnerSearchTerm("");
    handleCloseModal();
  }

  function handleClearOwner() {
    closeOwnerDropdown();
    unlockOwnerSearch();
    setProperty((prev) => ({
      ...prev,
      user_id: undefined,
      partner_broker_id: undefined,
      user_data: undefined,
    }));
    setOwnerEmailDisplay("");
    setOwnerSearchTerm("");
  }

  async function handleGenerateDescription() {
    try {
      if (briefDescription.length < 5) {
        addToast({ title: "Digite ao menos 5 caracteres", type: "error" });
        return;
      }

      setIsGeneratingDescription(true);

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;

      const payload: any = {
        brief_description: briefDescription,
      };

      if (property.id) {
        payload.property_id = property.id;
      } else {
        payload.title = property.title;
        payload.rooms = property.rooms;
        payload.bathrooms = property.bathrooms;
        payload.garages = property.garages;
        payload.area = property.area;
        payload.property_type_id = property.property_type_id;
      }

      const response = await api.post(
        "/api/adm/properties/generate-description-chatgpt",
        payload
      );

      if (response.data?.description) {
        setProperty((prev) => ({
          ...prev,
          description: response.data.description,
        }));

        addToast({
          title: "Descrição gerada com sucesso!",
          type: "success",
        });
      }
    } catch (err: any) {
      console.log(err);
      addToast({
        title: "Erro ao gerar descrição",
        type: "error",
      });
    }

    setIsGeneratingDescription(false);
  }

  function handleCloseModal() {
    dialog.current?.close();
  }

  async function handleSubmit() {
    setIsSubmiting(true);
    const formattedProperty: Record<string, unknown> = {
      ...property,
      address_id,
      area: Number(property.area.replaceAll("m²", "").replaceAll(",", ".")),
      garbage_tax: formatCurrency(property.garbage_tax),
      iptu_value: formatCurrency(property.iptu_value),
      rent_value: formatCurrency(property.rent_value),
      condominium_value: formatCurrency(property.condominium_value),
    };
    try {
      const selectedKeyLocationIdForValidation =
        formattedProperty.key_location_id != null
          ? Number(formattedProperty.key_location_id)
          : null;
      const othersErr = validateKeyLocationNoteForOthers(
        keyLocationCatalog,
        selectedKeyLocationIdForValidation,
        String(formattedProperty.key_location_note ?? "")
      );
      if (othersErr) {
        addToast({ title: othersErr, type: "error" });
        setIsSubmiting(false);
        return;
      }
      const passwordErr = validateKeyLocationPasswordForDigital(
        keyLocationCatalog,
        selectedKeyLocationIdForValidation,
        String(formattedProperty.key_location_password ?? "")
      );
      if (passwordErr) {
        addToast({ title: passwordErr, type: "error" });
        setIsSubmiting(false);
        return;
      }
      const nextCommissionErrors = validatePropertyCommission(
        commissionFormFromApi(property)
      );
      if (Object.keys(nextCommissionErrors).length > 0) {
        setCommissionErrors(nextCommissionErrors);
        addToast({
          title: firstCommissionError(nextCommissionErrors),
          type: "error",
        });
        setIsSubmiting(false);
        return;
      }
      setCommissionErrors({});

      const schema = Yup.object().shape({
        property_type_id: Yup.number().required("Informe o tipo do imóvel"),
        consultant_id: Yup.number().required("Consultante obrigatório"),
        title: Yup.string().required("Título obrigatório"),
        description: Yup.string().required("Descrição obrigatória"),
        rent_value: Yup.string().required("Valor do aluguel obrigatório"),
        condominium_value: Yup.string().required(
          "Valor do condomínio obrigatório"
        ),
        iptu_value: Yup.string().required("Valor do iptu obrigatório"),
        area: yupPropertyAreaString(),
        guarantees: Yup.array()
          .required("Mínimo de 1 garantia")
          .min(1, "Necessária ao menos 1 garantia"),
        condominium: Yup.string(),
        virtual_tour_url: Yup.string(),
        key_location_id: Yup.number()
          .nullable()
          .required("Localização da chave é obrigatória"),
        key_location_note: Yup.string()
          .max(255)
          .nullable()
          .when("key_location_id", {
            is: (id: number | null) =>
              isOthersKeyLocation(keyLocationCatalog, id ?? null),
            then: (schema) =>
              schema
                .trim()
                .required(
                  'Informe a observação da chave quando a opção é "Outros".'
                ),
            otherwise: (schema) => schema.nullable(),
          }),
        key_location_password: Yup.string()
          .nullable()
          .when("key_location_id", {
            is: (id: number | null) =>
              isDigitalKeyLocation(keyLocationCatalog, id ?? null),
            then: (schema) =>
              schema
                .trim()
                .required(KEY_LOCATION_PASSWORD_REQUIRED_MESSAGE),
            otherwise: (schema) => schema.nullable(),
          }),
        ...optionalAlternativeContactYupFields(),
      });

      await schema.validate(
        {
          ...formattedProperty,
          contact_email: String(property.contact_email ?? ""),
          contact_phone: String(property.contact_phone ?? ""),
        },
        {
          abortEarly: false,
        }
      );
      setAlternativeContactErrors({});

      if (!property.user_id && !property.user_data?.email) {
        addToast({
          title: "Selecione ou adicione um proprietário.",
          type: "error",
        });
        setIsSubmiting(false);
        return;
      }

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${!!token ? token : access_token
        }`;

      const selectedKeyLocationId =
        formattedProperty.key_location_id != null
          ? Number(formattedProperty.key_location_id)
          : null;
      const virtualTourUrlTrimmed =
        formattedProperty.virtual_tour_url != null
          ? String(formattedProperty.virtual_tour_url).trim()
          : "";
      const payloadForCreate = applyCommissionToPayload(
        applyKeyLocationToCreatePayload(
          {
            ...formattedProperty,
            description: sanitizePropertyDescriptionField(
              formattedProperty.description
            ),
            key_location_id: selectedKeyLocationId,
            virtual_tour_url:
              virtualTourUrlTrimmed !== "" ? virtualTourUrlTrimmed : null,
          },
          keyLocationCatalog
        ),
        commissionFormFromApi(property)
      );

      const draftPayload = applyPropertyAlternativeContactToPayload(
        buildPropertyCreatePayload(
          payloadForCreate,
          property.condominium,
          {
            user_id: property.user_id,
            partner_broker_id: property.partner_broker_id,
            user_data: property.user_data,
          }
        ),
        {
          contact_email: String(property.contact_email ?? ""),
          contact_phone: String(property.contact_phone ?? ""),
        }
      );

      const result = await saveDraft(
        api,
        draftPayload,
        propertyID ?? null
      );

      if (result.id) {
        onPropertyIdReceived(result.id);
        syncOwnerFromDraftSave(result.user_id);

        try {
          await syncKeyLocationIfNeeded(result.id);
          await syncLocalChildrenIfNeeded(result.id);
        } catch (syncErr: any) {
          addToast({
            title:
              syncErr?.response?.data?.message ??
              "Rascunho criado, mas falhou ao sincronizar chaves/filhos.",
            type: "error",
          });
        }

        onChangeCurrentForm();
        addToast({
          title: "Descrição salva no rascunho!",
          type: "success",
        });
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        const keys =
          error && typeof error === "object" ? Object.keys(error) : [];
        if (keys.length > 0) {
          setAlternativeContactErrors(
            Object.fromEntries(
              keys
                .filter(
                  (key) => key === "contact_email" || key === "contact_phone"
                )
                .map((key) => [key, error[key]])
            )
          );
          addToast({
            title: error[keys[0]],
            type: "error",
            description: "",
          });
        }
      } else {
        const apiErrors = err?.response?.data?.errors;
        if (
          apiErrors != null &&
          typeof apiErrors === "object" &&
          !Array.isArray(apiErrors)
        ) {
          const errorKeys = Object.keys(apiErrors);
          for (let i = 0; i < errorKeys.length; i++) {
            const messages = apiErrors[errorKeys[i]];
            if (Array.isArray(messages)) {
              messages.forEach((msg: string) => {
                addToast({ title: msg, type: "error" });
              });
            }
          }
        }
      }

      if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }
    }

    setIsSubmiting(false);
  }

  async function handleNextPropertyData() {
    try {
      const schema = Yup.object().shape({
        property_type_id: Yup.number().required("Informe o tipo do imóvel"),
        rent_value: Yup.string().required("Valor do aluguel obrigatório"),
        condominium_value: Yup.string().required(
          "Valor do condomínio obrigatório"
        ),
        iptu_value: Yup.string().required("Valor do iptu obrigatório"),
        area: yupPropertyAreaString(),
        guarantees: Yup.array()
          .required("Mínimo de 1 garantia")
          .min(1, "Necessária ao menos 1 garantia"),
      });

      const nextCommissionErrors = validatePropertyCommission(
        commissionFormFromApi(property)
      );
      if (Object.keys(nextCommissionErrors).length > 0) {
        setCommissionErrors(nextCommissionErrors);
        addToast({
          title: firstCommissionError(nextCommissionErrors),
          type: "error",
        });
        return;
      }
      setCommissionErrors({});

      await schema.validate(
        {
          ...property,
          area: String(property.area ?? ""),
          rent_value: String(property.rent_value ?? ""),
          condominium_value: String(property.condominium_value ?? ""),
          iptu_value: String(property.iptu_value ?? ""),
        },
        { abortEarly: false }
      );
      onGoToPublication();
      window.scrollTo(0, 0);
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        const keys =
          error && typeof error === "object" ? Object.keys(error) : [];
        if (keys.length > 0) {
          addToast({
            title: error[keys[0]],
            type: "error",
            description: "",
          });
        }
      }
    }
  }

  return (
    <>
      <ModalAddOwner
        onAddOwner={handleNewOwnerFromModal}
        onClose={handleCloseModal}
        owner={property.user_data}
        ref={dialog}
      />
      <div className="w-full min-w-0">
        <div className="mb-2 flex w-full justify-end">
          <AutoSaveIndicator
            isAutoSaving={isAutoSaving}
            hasUnsavedChanges={hasUnsavedChanges}
            hasError={hasError}
            lastSavedAt={lastSavedAt}
          />
        </div>

        {phase === "characteristics" ? (
          <CharacteristicsForm
            propertyID={propertyID}
            initialBenefits={initialProperty}
            rooms={property.rooms}
            suites={property.suites}
            bathrooms={property.bathrooms}
            garages={property.garages}
            condominium={property.condominium}
            permissionFlags={{
              is_furnished: property.is_furnished,
              allow_pets: property.allow_pets,
              water_included: property.water_included,
              gas_included: property.gas_included,
              is_adm: property.is_adm,
            }}
            isSavingPropertyDraft={isSavingDraft}
            onChangeProperty={handleChange}
            onSavePropertyDraft={performDraftSave}
            onBack={() => {
              onGoToPublication();
              window.scrollTo(0, 0);
            }}
            onNext={() => {
              onGoToPhotos();
              window.scrollTo(0, 0);
            }}
          />
        ) : phase === "property" ? (
          <FormStack>
            <ClassificationSection
              propertyTypeId={property.property_type_id}
              floorType={property.floor_type}
              housingProgram={property.housing_program}
              rank={property.rank}
              zapPublicationType={String(property.zap_publication_type ?? "STANDARD")}
              yearBuilt={property.year_built}
              virtualTourUrl={property.virtual_tour_url}
              onChange={handleChange}
            />
            <ValuesSection
              values={{
                rent_value: property.rent_value,
                condominium_value: property.condominium_value,
                iptu_value: property.iptu_value,
                garbage_tax: property.garbage_tax,
              }}
              area={property.area}
              onChange={handleChange}
            />
            <CommissionSection
              value={commissionFormFromApi(property)}
              errors={commissionErrors}
              onChange={(next: PropertyCommissionForm) => {
                setCommissionErrors({});
                setProperty((prev) => ({ ...prev, ...next }));
                markChanged();
              }}
            />
            <GuaranteesOnlySection
              selectedGuarantees={property.guarantees}
              onChange={handleGuaranteeChange}
            />
          </FormStack>
        ) : (
          <FormStack>
            <ResponsiblesSection
              hasSelectedOwner={hasSelectedOwner}
              ownerInputValue={
                property.user_data?.email ??
                (property.user_id ? ownerEmailDisplay : ownerSearchTerm)
              }
              ownerName={property.user_data?.first_name}
              ownerPhone={property.user_data?.phone}
              isFetchingOwners={isFetchingOwners}
              isOwnerDropdownOpen={isOwnerDropdownOpen}
              owners={owners}
              consultantsOptions={consultantsOptions}
              consultantValue={deriveConsultant(property.consultant_id, consultants)}
              isFetchingConsultants={isFetchingConsultants}
              findersOptions={findersOptions}
              finderValue={findersOptions.find((opt) => opt.value === property.finder_id)}
              loadingFinders={loadingFinders}
              onOwnerSearchChange={onSearchInputChange}
              onOwnerSearchTermChange={setOwnerSearchTerm}
              onSelectOwner={(id) => handleSelectOwner(id)}
              onClearOwner={handleClearOwner}
              onOpenNewOwnerModal={handleOpenNewOwnerModal}
              onCloseOwnerDropdown={closeOwnerDropdown}
              onConsultantChange={(id) => handleChange("consultant_id", id)}
              onFinderChange={(id) => handleChange("finder_id", id)}
              keyLocationCatalog={keyLocationCatalog}
              keyLocationId={property.key_location_id}
              keyLocationNote={property.key_location_note}
              keyLocationPassword={String(property.key_location_password ?? "")}
              onKeyLocationChange={(id) => handleChange("key_location_id", id)}
              onKeyLocationNoteChange={(value) =>
                handleChange("key_location_note", value)
              }
              onKeyLocationPasswordChange={(value) =>
                handleChange("key_location_password", value)
              }
              contactEmail={String(property.contact_email ?? "")}
              contactPhone={String(property.contact_phone ?? "")}
              contactEmailError={alternativeContactErrors.contact_email}
              contactPhoneError={alternativeContactErrors.contact_phone}
              onContactEmailChange={(value) => {
                setAlternativeContactErrors((current) => {
                  const next = { ...current };
                  delete next.contact_email;
                  return next;
                });
                handleChange("contact_email", value);
              }}
              onContactPhoneChange={(value) => {
                setAlternativeContactErrors((current) => {
                  const next = { ...current };
                  delete next.contact_phone;
                  return next;
                });
                handleChange("contact_phone", value);
              }}
            />
            <PublicationSection
              title={property.title}
              briefDescription={briefDescription}
              description={property.description}
              note={property.note}
              isGeneratingDescription={isGeneratingDescription}
              onTitleChange={(value) => handleChange("title", value)}
              onBriefDescriptionChange={setBriefDescription}
              onDescriptionChange={(value) => handleChange("description", value)}
              onGenerateDescription={handleGenerateDescription}
              onNoteChange={(value) => handleChange("note", value)}
            />
          </FormStack>
        )}

        {phase !== "characteristics" ? (
          <PropertyStepActions
            leading={
              <>
                <ResetButton
                  type="button"
                  onClick={() => {
                    setProperty((prevValue) => ({
                      ...prevValue,
                      ...defaultProperty,
                    }));
                    setOwnerEmailDisplay("");
                    setOwnerSearchTerm("");
                    closeOwnerDropdown();
                    unlockOwnerSearch();
                  }}
                >
                  LIMPAR
                </ResetButton>
                <SaveDraftButton
                  isLoading={isSavingDraft}
                  onClick={handleSaveDraft}
                />
              </>
            }
            trailing={
              <>
                {phase === "publication" ? (
                  <NextButton
                    type="button"
                    className="inline-flex h-10 gap-1.5 py-0 [&>svg]:!ml-0"
                    onClick={() => {
                      onGoToProperty();
                      window.scrollTo(0, 0);
                    }}
                  >
                    <ArrowLeft className="h-4 w-4 text-white" aria-hidden />
                    <p>Voltar</p>
                  </NextButton>
                ) : null}
                <NextButton
                  type="button"
                  className="inline-flex h-10 gap-1.5 py-0"
                  onClick={
                    isSubmiting
                      ? () => {}
                      : phase === "property"
                      ? handleNextPropertyData
                      : handleSubmit
                  }
                >
                  <p>{isSubmiting ? "Carregando..." : "Próximo"}</p>
                  {!isSubmiting && (
                    <ArrowRight className="h-4 w-4 text-white" aria-hidden />
                  )}
                </NextButton>
              </>
            }
          />
        ) : null}
      </div>
    </>
  );
}

export default DescriptionForm;
